import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { LegacyAuthService } from './legacy-auth.service';
import { CognitoAuthService } from './cognito-auth.service';
import { BaseAuthService } from './base-auth.service';
import { AuthState } from '../model/AuthState';
import { RegisterProps } from '../model/RegisterProps';
import { IdpProvider } from '../model/IdpProvider';

@Injectable()
export class ReflauntAuthService implements BaseAuthService {
  authState: AuthState = {
    isAuthenticated: false
  }
  stateChangedObservable?: Subject<AuthState>;

  constructor(private cognitoAuthService: CognitoAuthService, private legacyAuthService: LegacyAuthService) {
    if (this.legacyAuthService.hasOldToken()) {
      this.authState = this.cognitoAuthService.authState;
    } else {
      this.authState = this.cognitoAuthService.authState;
    }
    this.stateChangedObservable = this.cognitoAuthService.stateChangedObservable;
    this.cognitoAuthService.stateChangedObservable.subscribe(() => {
      this.authState = this.cognitoAuthService.authState;
    });
  }

  listenToStateChanges(): Observable<AuthState> {
    return this.cognitoAuthService.listenToStateChanges();
  }

  isAuthenticated(): Observable<AuthState> {
    if (this.legacyAuthService.hasOldToken()) {
      return this.legacyAuthService.isAuthenticated();
    } else {
      return this.cognitoAuthService.isAuthenticated();
    }
  }

  getAccessToken(): Observable<string> {
    if (this.legacyAuthService.hasOldToken()) {
      return this.legacyAuthService.getAccessToken();
    } else {
      return this.cognitoAuthService.getAccessToken();
    }
  }

  authenticateFromExternalIdp(idpProvider: IdpProvider): Promise<void> {
    return this.cognitoAuthService.authenticateFromExternalIdp.apply(this.cognitoAuthService, [...arguments]);
  }

  login(email: string, password: string): Promise<void> {
    return this.cognitoAuthService.login.apply(this.cognitoAuthService, [...arguments]);
  }

  register(props: RegisterProps): Promise<void> {
    return this.cognitoAuthService.register.apply(this.cognitoAuthService, [...arguments]);
  }

  forgotPassword(email): Promise<void> {
    return this.cognitoAuthService.forgotPassword.apply(this.cognitoAuthService, [...arguments]);
  }

  changePassword(old_password, new_password): Promise<void> {
    return this.cognitoAuthService.changePassword.apply(this.cognitoAuthService, [...arguments]);
  }

  async changePasswordAsAdmin(new_password: string, user_id: number): Promise<void> {
    return this.cognitoAuthService.changePasswordAsAdmin.apply(this.cognitoAuthService, [...arguments]);
  }

  resetPassword(email, code, password): Promise<void> {
    return this.cognitoAuthService.resetPassword.apply(this.cognitoAuthService, [...arguments]);
  }

  getUserProfile(): Promise<any> {
    return this.cognitoAuthService.getUserProfile.apply(this.cognitoAuthService, [...arguments]);
  }

  checkIfCognitoUserExist(): Promise<any> {
    return this.cognitoAuthService.checkIfCognitoUserExist.apply(this.cognitoAuthService, [...arguments]);
  }

  async logout(): Promise<void> {
    await this.legacyAuthService.logout();
    await this.cognitoAuthService.logout();
  }

  updateEmail(data: any): Promise<any> {
    return this.cognitoAuthService.updateEmail.apply(this.cognitoAuthService, [...arguments]);
  }

  verifyUpdateEmail(data: any): Promise<any> {
    return this.cognitoAuthService.verifyUpdateEmail.apply(this.cognitoAuthService, [...arguments]);
  }
}
